import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { ITTDSubAdvertiser } from '@feathr/blackbox';
import type { ITanStackColumnMeta } from '@feathr/components';
import { Button, ImageThumbnailCell, TanStackHeader } from '@feathr/components';

import AdvertiserOptionsCell from './AdvertiserOptionsCell';

import noImg from 'images/no-img.png';

const LOGO_SIZE = 40;

type TCell = CellContext<ITTDSubAdvertiser, unknown>;

/** Each sortable column `id` is the field `Advertisers` sorts the rows by. */
function advertisersColumns(t: TFunction): Array<ColumnDef<ITTDSubAdvertiser>> {
  return [
    {
      cell: ({ row }: TCell): JSX.Element => (
        <Observer>
          {(): JSX.Element => (
            <ImageThumbnailCell
              alt={t('{{name}} logo', { name: row.original.name })}
              height={LOGO_SIZE}
              src={row.original.logo || noImg}
              width={LOGO_SIZE}
            />
          )}
        </Observer>
      ),
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Logo')} />,
      id: 'logo',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      size: 80,
    },
    {
      accessorFn: (row): string => row.name ?? '',
      cell: ({ row }: TCell): JSX.Element => (
        <Observer>{(): JSX.Element => <>{row.original.name}</>}</Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Advertiser')} />,
      id: 'name',
      meta: { isFlex: true, isPrimary: true } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      accessorFn: (row): string => row.url ?? '',
      cell: ({ row }: TCell): JSX.Element => (
        <Observer>
          {(): JSX.Element => (
            <Button
              href={row.original.url}
              rel={'noopener noreferrer'}
              target={'_blank'}
              type={'link'}
            >
              {row.original.url}
            </Button>
          )}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Website URL')} />,
      id: 'url',
      meta: { isFlex: true } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      cell: ({ row }: TCell): JSX.Element => <AdvertiserOptionsCell original={row.original} />,
      enableSorting: false,
      header: (): JSX.Element => <TanStackHeader title={t('Options')} />,
      id: 'options',
      meta: { align: 'center', pinned: 'right' } satisfies ITanStackColumnMeta,
      size: 80,
    },
  ];
}

export default advertisersColumns;
